import type { Question } from '@/types/database';
import { logger } from '@/lib/logger';
import { getOnboardingGradingError } from '@/lib/errors/onboardingGradingErrors';
import { getPracticeError } from '@/lib/errors/practiceErrors';
import type { FriendlyError } from '@/lib/errors/types';
import { captureFriendlyError } from '@/lib/sentry';
import api from './client';
import type { ApiError } from './client';

// In-memory cache for questions (persists for session)
let questionsCache: Question[] | null = null;

interface QuestionsResponse {
  data: {
    questions?: Question[];
  };
}

interface RandomOnboardingQuestionResponse {
  success: boolean;
  data: {
    question: Question;
  };
}

export interface RandomOnboardingQuestionResult {
  success: boolean;
  question?: Question;
  error?: FriendlyError;
}

/**
 * Fetch all questions from the cached API endpoint
 * Results are cached in memory for the session
 */
export async function fetchAllQuestions(): Promise<Question[]> {
  // Return cached questions if available
  if (questionsCache) {
    return questionsCache;
  }

  try {
    const { data } = await api.get<QuestionsResponse>('/api/questions');
    const questions = (
      Array.isArray(data) ? data : data.questions || []
    ) as Question[];

    // Cache the questions for subsequent calls
    questionsCache = questions;
    return questions;
  } catch (error) {
    const apiError = error as ApiError;
    const code = apiError.code || 'questions_load_failed';
    const friendly = getPracticeError(code);
    const context = { fn: 'fetchAllQuestions', apiCode: code };
    logger.error('Error fetching questions', error, context);
    captureFriendlyError(error, friendly, context);
    return [];
  }
}

/**
 * Clear the in-memory questions cache
 * Call this if you need to force a refresh
 */
export function clearQuestionsCache(): void {
  questionsCache = null;
}

/**
 * Fetch a single question by ID
 * @param questionId - The question ID (1-128)
 * @returns The question or null if not found
 */
export async function fetchQuestion(
  questionId: number,
): Promise<Question | null> {
  const questions = await fetchAllQuestions();
  return questions.find((q) => q.id === questionId && q.is_active) || null;
}

/**
 * Fetch a randomly-selected onboarding civics question.
 * Backend excludes 65/20-rule questions and returns a fresh pick on each call.
 */
export async function fetchRandomOnboardingQuestion(): Promise<RandomOnboardingQuestionResult> {
  try {
    const result = await api.get<RandomOnboardingQuestionResponse>(
      '/api/questions/random-onboarding',
    );
    return { success: true, question: result.data?.question };
  } catch (error) {
    const apiError = error as ApiError;
    const code = apiError.code || 'question_load_failed';
    const friendly = getOnboardingGradingError(code);
    const context = { fn: 'fetchRandomOnboardingQuestion', apiCode: code };
    logger.error('Error fetching random onboarding question', error, context);
    captureFriendlyError(error, friendly, context);
    return { success: false, error: friendly };
  }
}

/**
 * Fetch multiple random questions for a test
 * @param count - Number of questions to fetch (default 20)
 * @returns Array of random questions
 */
export async function fetchRandomQuestions(
  count: number = 20,
): Promise<Question[]> {
  const questions = await fetchAllQuestions();
  const activeQuestions = questions.filter((q) => q.is_active);

  // Shuffle and take the requested count
  const shuffled = shuffleArray(activeQuestions);
  return shuffled.slice(0, count);
}

/**
 * Fetch questions by category
 * @param category - The question category
 * @returns Array of questions in that category
 */
export async function fetchQuestionsByCategory(
  category: Question['category'],
): Promise<Question[]> {
  const questions = await fetchAllQuestions();
  return questions.filter((q) => q.category === category && q.is_active);
}

/**
 * Fetch the 65/20 questions (20 easier questions for seniors)
 * @returns Array of 65/20 questions
 */
export async function fetch6520Questions(): Promise<Question[]> {
  const questions = await fetchAllQuestions();
  return questions.filter((q) => q.is_65_20_question && q.is_active);
}

/**
 * Fisher-Yates shuffle algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
